'use client';

import type { NavigationItem } from '@/components/layout/constants/navigation-constants';
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@/components/ui/sidebar';
import { useFeature } from '@/hooks/use-feature';
import { useChromePathname } from '@/shared/components/providers/chrome-pathname';
import { useAuthStore } from '@/shared/stores/auth-store';
import { ChevronRight, DollarSign, UserPlus } from 'lucide-react';
import Link from 'next/link';
import { tourAnchor } from '@/shared/lib/tours/anchors';
import { NavMessagesItem } from './nav-messages';

interface NavUserProps {
  items: NavigationItem[];
}

export function NavUser({ items }: NavUserProps) {
  // Null at prerender/first render → no active state; the pre-paint fix-up
  // script stamps data-active from data-active-match before first paint.
  const pathname = useChromePathname();
  const { user, isAuthenticated } = useAuthStore();
  const bookmarksEnabled = useFeature('BOOKMARK_SYSTEM');
  const historyEnabled = useFeature('WATCH_HISTORY');
  const inviteEnabled = useFeature('INVITE_SYSTEM');
  const isCreator = user?.is_creator;

  if (!isAuthenticated) return null;

  return (
    <SidebarGroup>
      <SidebarGroupLabel>
        <span>You</span>
        <ChevronRight className="ml-1 w-4 h-4" />
      </SidebarGroupLabel>
      <SidebarGroupContent>
        <SidebarMenu>
          {items.map((item) => {
            if (item.name === 'Watchlist' && !bookmarksEnabled) return null;
            if (item.name === 'History' && !historyEnabled) return null;
            const isActive =
              pathname !== null &&
              (pathname === item.href || pathname.startsWith(item.href.split('?')[0] + '/'));
            return (
              <SidebarMenuItem key={item.name}>
                <SidebarMenuButton asChild isActive={isActive} tooltip={item.name}>
                  <Link
                    href={item.href}
                    data-active-match="prefix"
                    suppressHydrationWarning
                    {...(item.tourAnchor ? tourAnchor(item.tourAnchor) : {})}
                  >
                    <item.icon />
                    <span>{item.name}</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            );
          })}

          <NavMessagesItem />

          {inviteEnabled && (
            <SidebarMenuItem>
              <SidebarMenuButton
                asChild
                isActive={pathname === '/account/invite'}
                tooltip="Invite a Friend"
              >
                <Link href="/account/invite" data-active-match="exact" suppressHydrationWarning>
                  <UserPlus />
                  <span>Invite a Friend</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          )}

          {isCreator && (
            <SidebarMenuItem>
              <SidebarMenuButton
                asChild
                isActive={pathname !== null && pathname.startsWith('/studio')}
                tooltip="Creator Studio"
              >
                <Link href="/studio" data-active-match="prefix" suppressHydrationWarning>
                  <DollarSign className="text-red-primary" />
                  <span>Creator Studio</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          )}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  );
}
